import type { Breadcrumb, ErrorEvent, StackFrame } from '@sentry/browser'
import type {
  Envelope,
  SerializedSession,
  SessionAggregates,
} from '@sentry/core'

// Approved identity and runtime context; arbitrary nested scope data stays excluded.
const USER_FIELDS = ['id', 'email', 'username', 'name'] as const
const BROWSER_FIELDS = ['name', 'version'] as const
const OS_FIELDS = ['name', 'version', 'build', 'kernel_version'] as const
const DEVICE_FIELDS = [
  'name',
  'family',
  'model',
  'model_id',
  'arch',
  'manufacturer',
  'brand',
  'orientation',
  'screen_resolution',
  'screen_height_pixels',
  'screen_width_pixels',
  'online',
] as const

function safeString(value: unknown, maxLength = 2048): string | undefined {
  return typeof value === 'string' &&
    value.length <= maxLength &&
    !/[\u0000-\u001f\u007f]/.test(value)
    ? value
    : undefined
}

/** Reporting metadata is limited to code-owned labels, resources and numeric diagnostics. */
export function reportingContext(input: Record<string, unknown> = {}) {
  const result: Record<string, string | number | boolean> = {}
  if (!input || typeof input !== 'object') return result
  for (const key of ['component', 'operation', 'errorType']) {
    const value = input[key]
    if (
      typeof value === 'string' &&
      /^[A-Za-z][A-Za-z0-9_.]{0,79}$/.test(value)
    )
      result[key] = value
  }
  const resources = {
    databaseId: input.databaseId ?? input.appId,
    orgId: input.orgId ?? input.organizationId,
    orgName: input.orgName ?? input.organizationName,
  }
  for (const [key, value] of Object.entries(resources)) {
    const text = safeString(value, 256)
    if (text?.trim()) result[key] = text
  }
  for (const key of ['statusCode', 'durationMs', 'retryCount']) {
    const value = input[key]
    if (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= 0 &&
      (key !== 'statusCode' ||
        (Number.isInteger(value) && value >= 100 && value <= 599))
    )
      result[key] = value
  }
  if (typeof input.success === 'boolean') result.success = input.success
  return result
}

/** One grouping policy for the helper and outgoing SDK events. */
export function reportingFingerprint(context: Record<string, unknown>) {
  const safe = reportingContext(context)
  return [
    String(safe.operation || 'frontend.error'),
    ...(safe.errorType ? [String(safe.errorType)] : []),
    ...(typeof safe.statusCode === 'number' ? [String(safe.statusCode)] : []),
    '{{ default }}',
  ]
}

function codeLocation(filename?: string) {
  const asset = filename
    ?.split(/[?#]/, 1)[0]
    .replace(/\\/g, '/')
    .match(
      /(?:^|\/)((?:assets|src|node_modules)\/[A-Za-z0-9_./-]+\.(?:[cm]?js|tsx?))$/,
    )?.[1]
  return asset && !asset.split('/').includes('..')
    ? `app:///${asset}`
    : undefined
}

function codeFrame(frame: StackFrame): StackFrame {
  return {
    filename: codeLocation(frame.filename),
    lineno: frame.lineno,
    colno: frame.colno,
    in_app: frame.in_app,
  }
}

export function sanitizeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // Automatic DOM, URL, network and console breadcrumbs can contain user input.
  if (
    breadcrumb.category !== 'async.operation' ||
    !/^(Starting|Completed|Failed) [A-Za-z][A-Za-z0-9_.]{0,79}$/.test(
      breadcrumb.message || '',
    )
  )
    return null
  return {
    category: 'async.operation',
    message: breadcrumb.message,
    level: breadcrumb.level,
    timestamp: breadcrumb.timestamp,
    data: reportingContext(breadcrumb.data),
  }
}

/** Omit balanced bodies and the entire remainder of truncated or malformed ones. */
function redactPayloads(value: string): string {
  let result = ''
  let cursor = 0
  let start = -1
  let quote: string | undefined
  const closing: string[] = []
  for (let index = 0; index < value.length; index++) {
    const character = value[index]
    if (start < 0) {
      if (character !== '{' && character !== '[') continue
      start = index
    }
    if (quote) {
      if (character === '\\') index++
      else if (character === quote) quote = undefined
      continue
    }
    if (character === '"' || character === "'" || character === '`') {
      quote = character
    } else if (character === '{' || character === '[') {
      closing.push(character === '{' ? '}' : ']')
    } else if (character === '}' || character === ']') {
      if (character !== closing.pop())
        return result + value.slice(cursor, start) + '[payload omitted]'
      if (closing.length === 0) {
        result += value.slice(cursor, start) + '[payload omitted]'
        cursor = index + 1
        start = -1
      }
    }
  }
  return start < 0
    ? result + value.slice(cursor)
    : result + value.slice(cursor, start) + '[payload omitted]'
}

const credentialKeyPattern =
  /(?:access|refresh|http|shared|playground)[-_]?token|client[-_]?secret|private[-_]?key|api[-_]?key(?:[-_]?secret)?|secret|cluster[-_]?ca[-_]?cert(?:[-_]?password)?|user[-_]?ca[-_]?cert(?:[-_]?password)?|client[-_]?config|connection[-_]?string|password|passwd|passphrase|aws[-_]?secrets[-_]?manager|offline[-_]?only[-_]?license[-_]?token|certificate|token/
    .source

const credentialHeaderKeyPattern =
  /(?:proxy[-_]?)?authorization|(?:set[-_]?)?cookie/.source
const quotedCredentialKeyPattern = new RegExp(
  String.raw`(["'\x60])\s*(${credentialHeaderKeyPattern}|${credentialKeyPattern})\s*\1(?=\s*[:=])`,
  'gi',
)

function normalizeQuotedCredentialKeys(value: string): string {
  return value.replace(
    quotedCredentialKeyPattern,
    (_match, _quote: string, key: string, offset: number) =>
      `${offset > 0 && /\w/.test(value[offset - 1]) ? ' ' : ''}${key}`,
  )
}

// Quoted values are atomic: URL/path/credential replacements must not alter
// their escaped delimiters before the complete value has been removed.
// Credential names include common camelCase, snake_case and kebab-case spellings.
const messageRedactions: ReadonlyArray<readonly [RegExp, string]> = [
  // Remove whole PEM blocks before quoted headers can hide their opening marker.
  [
    /-----BEGIN [A-Z0-9 ]+(?:-----|$)[\s\S]*?(?:-----END [A-Z0-9 ]+-----|$)/gi,
    '[Filtered]',
  ],
  // The non-word boundary keeps contractions intact without lookbehind:
  // this module loads before browser-support detection.
  [
    /"(?:\\[\s\S]|[^"\\])*(?:"|\\?$)|\B'(?:\\[\s\S]|[^'\\])*(?:'|\\?$)|`(?:\\[\s\S]|[^`\\])*(?:`|\\?$)/g,
    '[Filtered]',
  ],
  [/\b(?:https?|mongodb(?:\+srv)?):\/\/[^\s"'<>]+/gi, '[URL omitted]'],
  // Credential headers may contain several schemes, cookies or directives.
  // Omit their entire remainder; retain diagnostic metadata separately.
  [
    new RegExp(
      String.raw`\b(${credentialHeaderKeyPattern})\b\s*[:=]\s*[\s\S]*`,
      'gi',
    ),
    '$1=[Filtered]',
  ],
  // Only a complete quoted/payload/PEM placeholder or a final numeric retry
  // field provides a clear boundary. Otherwise omit the ambiguous remainder,
  // including punctuation and whitespace that may be part of the secret.
  [
    new RegExp(
      String.raw`\b(${credentialKeyPattern})\b\s*[:=]\s*(?:\[(?:Filtered|payload omitted)\](?=\s|$)|[\s\S]*?(?=&retry=\d+$)|[\s\S]*)`,
      'gi',
    ),
    '$1=[Filtered]',
  ],
  // Basic auth/authentication introduces diagnostic prose. Explicit header
  // and credential assignments have already been scrubbed above.
  [
    /\b(Bearer|Basic(?!\s+auth(?:entication)?\b))\s+(?:\[Filtered\](?=\s|$)|[\s\S]*?(?=&retry=\d+$)|[\s\S]*)/gi,
    '$1 [Filtered]',
  ],
  [/\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[Filtered]'],
  [/[A-Za-z0-9+/]{80,}={0,2}/g, '[Filtered]'],
  [/[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[Filtered]'],
  [/(?:\/Users\/|\/home\/)[^\s"']+/g, '[path omitted]'],
  [/[\u0000-\u001f\u007f]/g, ' '],
]

function isIPv6Address(value: string): boolean {
  try {
    new URL(`http://[${value}]/`)
    return true
  } catch {
    return false
  }
}

/** Redact values, not words such as Unauthorized, auth0 or tokenExpiry (PORT-970). */
function redactMessage(value: string | undefined): string | undefined {
  if (!value) return undefined
  // Fail closed before regex work: cutting the input could expose truncated
  // credential/body fragments, and arbitrarily long inputs can be expensive.
  if (value.length > 8192) return '[message omitted: too long]'
  // Require statement clauses rather than English words like select or settings.
  // Known DQL operations omit all descriptions separately, including fragments.
  // The wildcard already consumes whitespace: repeated whitespace matchers on
  // either side introduce excessive backtracking when FROM is absent.
  if (
    /\b(?:select\s[\s\S]+?\sfrom\b|insert\s+into\s+\S+\s+(?:documents\b|values\b|select\b|[({\[])|update\s+\S+\s+set\b|delete\s+from\s+\S+|(?:alter|create|drop)\s+collection\s+\S+)/i.test(
      value,
    )
  )
    return '[query details omitted]'
  // Payloads must be removed before other rules insert bracketed placeholders.
  let message = normalizeQuotedCredentialKeys(redactPayloads(value))
  for (const [pattern, replacement] of messageRedactions)
    message = message.replace(pattern, replacement)
  // Find candidates without nested hextet repetition; the platform validates
  // IPv6 structure. Capture the left separator without requiring lookbehind.
  message = message.replace(
    /(^|[^\w:])([a-f0-9:](?:[a-f0-9:.]*[a-f0-9:])?)(?![\w:])/gi,
    (match, prefix: string, candidate: string) => {
      if (candidate.indexOf(':') === candidate.lastIndexOf(':')) return match
      if (isIPv6Address(candidate)) return `${prefix}[Filtered]`
      // A final diagnostic colon is not part of the address. Validate the
      // complete candidate first so valid compressed addresses retain priority.
      if (candidate.endsWith(':') && isIPv6Address(candidate.slice(0, -1)))
        return `${prefix}[Filtered]:`
      return match
    },
  )
  return message
    .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[Filtered]')
    .slice(0, 2048)
}

const dqlOperations = new Set([
  'executeDqlQuery',
  'executeRemoteQuery',
  'validateQuery',
])

/** Keep approved diagnostic fields without copying arbitrary nested scope data. */
function diagnosticFields(
  input: Record<string, unknown> | undefined,
  keys: readonly string[],
) {
  if (!input) return undefined
  return Object.fromEntries(
    keys.flatMap<[string, string | number | boolean]>((key) => {
      const value = input[key]
      const text = safeString(value)
      if (text !== undefined) return [[key, text]]
      return (typeof value === 'number' && Number.isFinite(value)) ||
        typeof value === 'boolean'
        ? [[key, value]]
        : []
    }),
  )
}

/** Keep page origin/path while omitting credentials, query text and fragments. */
function pageUrl(value?: string) {
  if (!value || value.length > 8192) return undefined
  try {
    const url = new URL(value)
    if (!['http:', 'https:'].includes(url.protocol)) return undefined
    url.username = ''
    url.password = ''
    url.search = ''
    url.hash = ''
    return url.href
  } catch {
    return undefined
  }
}

/** Applies to direct SDK calls as well as the shared reporting helpers. */
export function sanitizeEvent(event: ErrorEvent): ErrorEvent {
  const context = reportingContext({
    databaseId: event.contexts?.database?.id ?? event.tags?.database_id,
    orgId: event.contexts?.organization?.org_id ?? event.tags?.org_id,
    orgName: event.contexts?.organization?.org_name,
    ...reportingContext(event.tags),
    ...reportingContext(event.contexts?.error_context),
  })
  const operation = String(context.operation || 'frontend.error')
  const userAgent = safeString(
    event.request?.headers?.['User-Agent'] ||
      event.request?.headers?.['user-agent'],
  )
  // Only these code-owned operations execute customer DQL. A GraphQL operation
  // such as fetchMetricsQuery must retain its diagnostic text.
  const queryError =
    context.errorType === 'dql_error' || dqlOperations.has(operation)
  const messageFor = (value?: string) =>
    queryError
      ? `${operation}: ${context.errorType || 'query failure'} [query details omitted]`
      : redactMessage(value) || operation
  const message = messageFor(
    event.message || event.exception?.values?.at(-1)?.value,
  )
  return {
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: 'javascript',
    environment: event.environment,
    release: event.release,
    level: event.level,
    message,
    tags: context,
    user: diagnosticFields(event.user, USER_FIELDS),
    request: {
      url: pageUrl(event.request?.url),
      headers: userAgent ? { 'User-Agent': userAgent } : undefined,
    },
    contexts: {
      error_context: context,
      browser: diagnosticFields(event.contexts?.browser, BROWSER_FIELDS),
      os: diagnosticFields(event.contexts?.os, OS_FIELDS),
      device: diagnosticFields(event.contexts?.device, DEVICE_FIELDS),
    },
    fingerprint: reportingFingerprint(context),
    exception: event.exception
      ? {
          values: event.exception.values?.slice(-6).map((exception) => ({
            type: [
              'Error',
              'TypeError',
              'RangeError',
              'ReferenceError',
              'SyntaxError',
              'URIError',
              'EvalError',
              'AggregateError',
              'AxiosError',
              'RRNLRequestError',
            ].includes(exception.type || '')
              ? exception.type
              : 'Error',
            value: messageFor(exception.value),
            mechanism: {
              type:
                exception.mechanism?.type?.match(
                  /^[A-Za-z][A-Za-z0-9_.]{0,79}$/,
                )?.[0] || 'generic',
              handled: exception.mechanism?.handled ?? true,
              ...(Number.isInteger(exception.mechanism?.exception_id)
                ? { exception_id: exception.mechanism?.exception_id }
                : {}),
              ...(Number.isInteger(exception.mechanism?.parent_id)
                ? { parent_id: exception.mechanism?.parent_id }
                : {}),
              ...(exception.mechanism?.is_exception_group
                ? { is_exception_group: true }
                : {}),
            },
            stacktrace: exception.stacktrace
              ? { frames: exception.stacktrace.frames?.map(codeFrame) }
              : undefined,
          })),
        }
      : undefined,
    breadcrumbs: event.breadcrumbs?.flatMap((b) => {
      const safe = sanitizeBreadcrumb(b)
      return safe ? [safe] : []
    }),
    debug_meta: {
      images: event.debug_meta?.images?.flatMap((image) => {
        const code_file = codeLocation(image.code_file)
        return image.type === 'sourcemap' &&
          code_file &&
          /^[a-f0-9-]{36}$/i.test(image.debug_id || '')
          ? [{ type: 'sourcemap', code_file, debug_id: image.debug_id }]
          : []
      }),
    },
  }
}

/** Sessions bypass beforeSend. Strip identity before the offline queue or transport sees them. */
export function sanitizeSessionEnvelope(envelope: Envelope): Envelope {
  for (const item of envelope[1]) {
    if (item[0].type === 'session') {
      const session = item[1] as SerializedSession
      item[1] = {
        sid: session.sid,
        init: session.init,
        started: session.started,
        timestamp: session.timestamp,
        status: session.status,
        errors: session.errors,
        ...(session.duration !== undefined
          ? { duration: session.duration }
          : {}),
        attrs: {
          release: session.attrs?.release,
          environment: session.attrs?.environment,
        },
      }
    } else if (item[0].type === 'sessions') {
      const sessions = item[1] as SessionAggregates
      item[1] = {
        aggregates: sessions.aggregates.map(
          ({ started, exited, errored, crashed }) => ({
            started,
            exited,
            errored,
            crashed,
          }),
        ),
        attrs: {
          release: sessions.attrs?.release,
          environment: sessions.attrs?.environment,
        },
      }
    }
  }
  return envelope
}
